import {
  type Chain,
  type DynamicClient,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  getBuffer,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import {
  DYNAMIC_WAAS_METADATA,
  createWaasProvider,
  getAllUserWaasAddressesForChain,
} from '@dynamic-labs-sdk/client/waas/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { PublicKey } from '@solana/web3.js';

import { getSolanaConnection } from '../../../getSolanaConnection';
import { isVersionedTransaction } from '../../../utils/isVersionedTransaction';
import { solanaExecuteCheckoutTransaction } from '../../../utils/solanaExecuteCheckoutTransaction';
import { solanaTransferAmount } from '../../../utils/solanaTransferAmount';
import type { WaasSolanaWalletProvider } from '../../WaasSolanaWalletProvider.types';
import { sponsorTransaction } from '../sponsorTransaction';

export const createWalletProviderForWaasSolana = (
  sdkClient: DynamicClient
): WaasSolanaWalletProvider => {
  const chain: Chain = 'SOL';

  const walletProviderType = WalletProviderEnum.EmbeddedWallet;

  const key = formatWalletProviderKey({
    chain,
    displayName: DYNAMIC_WAAS_METADATA.displayName,
    walletProviderType,
  });

  const waasProvider = createWaasProvider({ chain, sdkClient });

  const getActiveNetworkId: WaasSolanaWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: sdkClient,
        walletProviderKey: key,
      });

  const switchActiveNetwork: WaasSolanaWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) =>
      switchActiveNetworkInLastKnownRegistry({
        client: sdkClient,
        networkId,
        walletProviderKey: key,
      });

  const getConnectedAddresses: WaasSolanaWalletProvider['getConnectedAddresses'] =
    async () => {
      const waasSolanaAddresses = getAllUserWaasAddressesForChain(
        { chain },
        sdkClient
      );

      return {
        addresses: waasSolanaAddresses,
      };
    };

  const signAndSendTransaction: WaasSolanaWalletProvider['signAndSendTransaction'] =
    async ({ transaction, walletAccount, options }) => {
      const { signedTransaction } = await signTransaction({
        transaction,
        walletAccount,
      });

      const { networkData } = await getActiveNetworkData(
        { walletAccount },
        sdkClient
      );

      assertDefined(networkData, 'Network data is required');

      const connection = getSolanaConnection({ networkData });

      const rawTransaction = signedTransaction.serialize();

      const signature = await connection.sendRawTransaction(
        rawTransaction,
        options
      );

      return { signature };
    };

  const signAndSendSponsoredTransaction: WaasSolanaWalletProvider['signAndSendSponsoredTransaction'] =
    async ({ transaction, walletAccount, options }) => {
      const sponsoredTx = await sponsorTransaction({ transaction }, sdkClient);

      const { signedTransaction } = await signTransaction({
        transaction: sponsoredTx,
        walletAccount,
      });

      const { networkData } = await getActiveNetworkData(
        { walletAccount },
        sdkClient
      );

      assertDefined(networkData, 'Network data is required');

      const connection = getSolanaConnection({ networkData });

      const rawTransaction = signedTransaction.serialize();

      // Skip preflight simulation to avoid false
      // "Attempt to debit an account but found no record of a prior credit"
      // errors — the fee payer has been replaced but preflight may still reject it
      const signature = await connection.sendRawTransaction(rawTransaction, {
        skipPreflight: true,
        ...options,
      });

      return { signature };
    };

  const signTransaction: WaasSolanaWalletProvider['signTransaction'] = async ({
    transaction,
    walletAccount,
  }) => {
    const messageBytes = isVersionedTransaction(transaction)
      ? transaction.message.serialize()
      : transaction.serializeMessage();

    const serializedTransaction = getBuffer()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from(messageBytes as any)
      .toString('hex');

    const { networkData } = await getActiveNetworkData(
      { walletAccount },
      sdkClient
    );

    const { signature } = await waasProvider.signSerializedTransaction({
      chainId: networkData?.networkId,
      serializedTransaction,
      walletAccount,
    });

    const publicKey = new PublicKey(walletAccount.address);

    transaction.addSignature(
      publicKey,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getBuffer().from(signature, 'hex') as any
    );

    transaction.serialize({
      requireAllSignatures: false,
      verifySignatures: true,
    });

    return { signedTransaction: transaction };
  };

  const signAllTransactions: WaasSolanaWalletProvider['signAllTransactions'] =
    async ({ transactions, walletAccount }) => {
      const signedTransactions = await Promise.all(
        transactions.map(async (transaction) => {
          const { signedTransaction } = await signTransaction({
            transaction,
            walletAccount,
          });

          return signedTransaction;
        })
      );

      return { signedTransactions };
    };

  const signMessage: WaasSolanaWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    return waasProvider.signMessage({ message, walletAccount });
  };

  return {
    ...waasProvider,
    chain,
    executeCheckoutTransaction: (args) =>
      solanaExecuteCheckoutTransaction(args, sdkClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(DYNAMIC_WAAS_METADATA.displayName),
    key,
    metadata: {
      displayName: DYNAMIC_WAAS_METADATA.displayName,
      icon: DYNAMIC_WAAS_METADATA.icon,
    },
    signAllTransactions,
    signAndSendSponsoredTransaction,
    signAndSendTransaction,
    signMessage,
    signTransaction,
    switchActiveNetwork,
    transferAmount: (args) => solanaTransferAmount(args, sdkClient),
    walletProviderType,
  };
};
